import { useState, useEffect, useCallback } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Props {
  template: EbookTemplate;
  onClose: () => void;
  onUse: () => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

// ─── Category-specific content ─────────────────────────────────────────────────
interface PageContent {
  chapterTitle: string;
  chapterSubtitle: string;
  introText: string;
  introHighlight: string;
  sections: string[];
  dataLabel: string;
  dataStats: { label: string; val: string; trend: string }[];
  summaryPoints: { icon: string; title: string; desc: string }[];
  tocChapters: string[];
  quote: string;
  quoteAuthor: string;
}

function getContent(category: string): PageContent {
  const map: Record<string, PageContent> = {
    'Business & Entrepreneurship': {
      chapterTitle: "The Founder's Operating System", chapterSubtitle: 'Chapter One',
      introText: 'Every successful company began with a single, clear idea — and a founder willing to act before they felt ready. This guide distills the patterns behind 500+ high-growth startups into a repeatable playbook you can execute starting today.',
      introHighlight: 'The best time to build was yesterday. The second-best time is right now.',
      sections: ['Validating Your Core Assumption', 'Building Your First Revenue Loop', 'Hiring for Culture & Capability', 'Scaling Without Breaking Things'],
      dataLabel: 'Growth Benchmarks', dataStats: [{ label: 'Avg. Revenue Growth', val: '+187%', trend: 'Year 1→2' }, { label: 'Customer Retention', val: '82%', trend: 'Top quartile' }, { label: 'Founders Surveyed', val: '500+', trend: 'Global sample' }, { label: 'Time to Product-Market Fit', val: '14 mo', trend: 'Median' }],
      summaryPoints: [{ icon: 'ri-rocket-line', title: 'Ship Fast', desc: 'Release your MVP in 30 days or less. Perfect is the enemy of launched.' }, { icon: 'ri-bar-chart-line', title: 'Measure Everything', desc: 'Define 3 north-star metrics and review them every Monday morning.' }, { icon: 'ri-team-line', title: 'Build Your Network', desc: 'Your next hire, investor, or customer is already in your network — find them.' }],
      tocChapters: ['The Founder Mindset', "Validating Your Idea", 'The Revenue Blueprint', 'Building Your First Team', 'Fundraising Strategies', 'Product-Market Fit Signals', 'Scaling Operations', 'Financial Modeling', 'Exit Strategies & M&A', 'Your 90-Day Plan'],
      quote: 'Ideas are cheap. The rarest commodity is a founder who ships.', quoteAuthor: 'Paul Graham, Y Combinator',
    },
    'Self-Help & Personal Development': {
      chapterTitle: 'Rewiring Your Brain for Success', chapterSubtitle: 'Chapter One',
      introText: "The latest neuroscience confirms what high performers have known for decades: your habits, not your talent, determine your outcomes. This guide teaches you the exact protocols used by Olympic athletes, Fortune 500 executives, and award-winning creatives.",
      introHighlight: 'You are not your circumstances — you are your daily decisions.',
      sections: ['The Habit Loop Decoded', 'Designing Your Environment for Win', 'Identity-Based Habit Formation', 'Tracking Progress Without Burnout'],
      dataLabel: 'Performance Benchmarks', dataStats: [{ label: 'Habit Retention Rate', val: '91%', trend: 'After 66 days' }, { label: 'Focus Improvement', val: '3.4×', trend: 'vs. baseline' }, { label: 'Energy Levels', val: '+68%', trend: 'Reported increase' }, { label: 'Goal Achievement', val: '79%', trend: 'vs 12% average' }],
      summaryPoints: [{ icon: 'ri-brain-line', title: 'Identity First', desc: 'Decide who you want to become, then take one action that proves it to yourself today.' }, { icon: 'ri-seedling-line', title: 'Small Wins Stack', desc: '1% better every day is 37× better by year-end. Compound gains are relentless.' }, { icon: 'ri-focus-3-line', title: 'Protect Your Energy', desc: 'Say no to anything that doesn\'t align with your top 3 priorities for this season.' }],
      tocChapters: ['The Identity Shift', 'Habit Architecture', 'Morning & Evening Rituals', 'Deep Focus Protocols', 'Emotional Regulation', 'Mindset vs. Fixed Beliefs', 'Building Resilience', 'The Energy Equation', 'Social Influence & Your Circle', 'Your 30-Day Reset Plan'],
      quote: 'You do not rise to the level of your goals — you fall to the level of your systems.', quoteAuthor: 'James Clear, Author of Atomic Habits',
    },
    'Technology & Programming': {
      chapterTitle: 'System Design Fundamentals', chapterSubtitle: 'Chapter One',
      introText: 'Whether you\'re building a side project or scaling a platform to millions of users, the principles of good software architecture remain constant. This guide walks through the patterns, trade-offs, and real-world decisions that define modern engineering excellence.',
      introHighlight: 'Write code for the next developer — that developer is usually you, six months later.',
      sections: ['Monolith vs. Microservices Trade-offs', 'Database Design & Indexing Strategy', 'API Design Patterns (REST, GraphQL)', 'Observability: Logs, Metrics & Traces'],
      dataLabel: 'Engineering KPIs', dataStats: [{ label: 'Test Coverage Target', val: '85%+', trend: 'Industry best practice' }, { label: 'Deploy Frequency', val: 'Daily', trend: 'Elite teams' }, { label: 'MTTR', val: '<1 hour', trend: 'Incident recovery' }, { label: 'Bundle Size Reduction', val: '42%', trend: 'With tree-shaking' }],
      summaryPoints: [{ icon: 'ri-code-s-slash-line', title: 'Keep It Simple', desc: 'The best architecture is the simplest one that solves the actual problem in front of you.' }, { icon: 'ri-shield-check-line', title: 'Test in Production Mindset', desc: 'Feature flags, canary deploys, and observability let you ship confidently at any scale.' }, { icon: 'ri-git-branch-line', title: 'Document as You Go', desc: 'A 3-minute README update today saves 3 hours of confusion for your future self.' }],
      tocChapters: ['Clean Code Principles', 'Data Structures Deep Dive', 'Algorithm Complexity', 'System Design Patterns', 'Database Architecture', 'API Best Practices', 'Security Fundamentals', 'Performance Optimization', 'CI/CD Pipelines', 'Mastering Git Workflows'],
      quote: 'Any fool can write code that a computer can understand. Good programmers write code that humans can understand.', quoteAuthor: 'Martin Fowler, Software Engineer',
    },
    'Health & Wellness': {
      chapterTitle: 'The Pillars of Lasting Health', chapterSubtitle: 'Chapter One',
      introText: 'Modern health research has unlocked a profound truth: your body is not your enemy — it\'s your most powerful ally. When you understand the four non-negotiable pillars of human health, everything changes. Energy returns. Clarity sharpens. Life expands.',
      introHighlight: 'Health is not the absence of disease — it\'s the presence of vitality.',
      sections: ['Sleep: The Foundation of Everything', 'Nutrition Without the Noise', 'Movement as Medicine', 'Stress as a Tool, Not a Threat'],
      dataLabel: 'Health Outcome Data', dataStats: [{ label: 'Energy Increase', val: '+71%', trend: 'After protocol' }, { label: 'Sleep Quality', val: '↑ 58%', trend: 'REM improvement' }, { label: 'Inflammation Markers', val: '↓ 34%', trend: 'CRP reduction' }, { label: 'Metabolic Health', val: '92%', trend: 'Improvement rate' }],
      summaryPoints: [{ icon: 'ri-zzz-line', title: 'Prioritize Sleep', desc: '7-9 hours of quality sleep is the single highest-ROI health intervention available to you.' }, { icon: 'ri-leaf-line', title: 'Eat Real Food', desc: 'If your great-grandmother wouldn\'t recognize it as food, your body probably won\'t either.' }, { icon: 'ri-run-line', title: 'Move Daily', desc: '30 minutes of walking reduces all-cause mortality more than any supplement on the market.' }],
      tocChapters: ['The Four Pillars Explained', 'Sleep Science & Optimization', 'Macronutrients Demystified', 'The Exercise Prescription', 'Gut Health & Microbiome', 'Hormonal Balance', 'Mental Health Connection', 'Longevity Protocols', 'Lab Tests You Need', 'Your 12-Week Reset'],
      quote: 'Take care of your body — it\'s the only place you have to live.', quoteAuthor: 'Jim Rohn',
    },
    'Finance & Investing': {
      chapterTitle: 'The Wealth-Building Blueprint', chapterSubtitle: 'Chapter One',
      introText: 'Wealth is not built through luck or a single windfall — it\'s the result of consistent decisions made across decades. This guide walks you through the exact financial architecture used by the top 1% of household earners, adapted for every income level.',
      introHighlight: 'Compound interest is the eighth wonder of the world. He who understands it, earns it.',
      sections: ['The Wealth Pyramid Framework', 'Emergency Fund Engineering', 'Index Investing vs. Active Strategy', 'Tax-Advantaged Accounts Maximized'],
      dataLabel: 'Investment Benchmarks', dataStats: [{ label: 'S&P 500 Avg. Return', val: '10.7%', trend: 'Annual (100yr avg)' }, { label: 'Compound Effect', val: '7.4×', trend: '30yr @ 7% avg' }, { label: 'Emergency Fund Target', val: '6 mo', trend: 'Of living expenses' }, { label: 'Optimal Savings Rate', val: '20%+', trend: 'Of gross income' }],
      summaryPoints: [{ icon: 'ri-money-dollar-circle-line', title: 'Pay Yourself First', desc: 'Automate savings before you touch your paycheck. Make wealth-building non-negotiable.' }, { icon: 'ri-line-chart-line', title: 'Stay the Course', desc: 'The biggest threat to your portfolio is your own behavior during market volatility.' }, { icon: 'ri-building-line', title: 'Diversify Wisely', desc: 'Own assets across stocks, bonds, real estate, and cash — in proportions that match your timeline.' }],
      tocChapters: ['Your Financial Baseline', 'Debt Destruction Strategy', 'The Investment Spectrum', 'Stock Market Fundamentals', 'Real Estate as an Asset', 'Tax Optimization', 'Retirement Planning', 'Estate & Legacy Planning', 'Side Income Streams', 'Your 5-Year Money Plan'],
      quote: 'Do not save what is left after spending, but spend what is left after saving.', quoteAuthor: 'Warren Buffett',
    },
    'Fiction & Storytelling': {
      chapterTitle: 'Into the Unknown', chapterSubtitle: 'Chapter One',
      introText: 'The village of Ashenveil had not seen rain in forty-seven days. The cracked earth yawned wide, splitting the market square like an open wound, and the elders — those who remembered the last drought — gathered every evening in the shade of the dying oak to speak in hushed voices about what was coming.',
      introHighlight: '"This land has a memory," the oldest among them said. "And what it remembers, it repeats."',
      sections: ['The Awakening', 'Shadows at the Border', 'A Voice from the Deep', 'The Price of Knowing'],
      dataLabel: 'Story Structure', dataStats: [{ label: 'Acts', val: '3', trend: 'Classic structure' }, { label: 'POV Characters', val: '4', trend: 'Woven narrative' }, { label: 'Chapters', val: '28', trend: 'Full arc' }, { label: 'Est. Read Time', val: '8 hrs', trend: '95k words' }],
      summaryPoints: [{ icon: 'ri-map-line', title: 'The World', desc: 'A richly imagined realm where magic is a resource — finite, contested, and deeply political.' }, { icon: 'ri-user-heart-line', title: 'The Characters', desc: 'Four strangers bound by a prophecy none of them chose, each carrying a secret that could destroy the others.' }, { icon: 'ri-fire-line', title: 'The Conflict', desc: 'As the drought deepens, the true cause is revealed — and it has been waiting beneath the earth for a thousand years.' }],
      tocChapters: ['The Drought Begins', 'Strangers in Ashenveil', 'The Elder\'s Warning', 'Underground Passages', 'The First Vision', 'Betrayal at the Wall', 'Into the Deep', 'The Ritual', 'The Price Revealed', 'A World Remade'],
      quote: 'A story is a character who wants something and will overcome obstacles to get it.', quoteAuthor: 'Kurt Vonnegut',
    },
    'Marketing & Sales': {
      chapterTitle: 'The Attention Economy Playbook', chapterSubtitle: 'Chapter One',
      introText: 'The average person now encounters over 10,000 brand messages per day — and remembers fewer than 10. In a world of infinite content and fractured attention, the marketers who win are not those with the biggest budget, but those with the most compelling story.',
      introHighlight: 'Marketing is no longer about the stuff you make, but about the stories you tell.',
      sections: ['The Attention → Trust → Purchase Loop', 'Content That Converts', 'Paid Media: ROI-Focused Approach', 'Community as a Moat'],
      dataLabel: 'Marketing Performance', dataStats: [{ label: 'Email Open Rate (avg)', val: '21.3%', trend: 'B2B benchmark' }, { label: 'Content ROI', val: '3.2×', trend: 'Organic vs. paid' }, { label: 'Customer Acquisition Cost', val: '↓ 44%', trend: 'Content-led growth' }, { label: 'Retention via Community', val: '89%', trend: 'Branded community' }],
      summaryPoints: [{ icon: 'ri-megaphone-line', title: 'Story First', desc: 'Before you write a single ad, know your customer\'s deepest frustration and biggest dream.' }, { icon: 'ri-mail-line', title: 'Own Your List', desc: 'Your email list is your only truly owned marketing channel — grow it like your business depends on it.' }, { icon: 'ri-group-line', title: 'Build in Public', desc: 'Share your process, your failures, and your wins. Authenticity converts better than polish.' }],
      tocChapters: ['Understanding Modern Buyers', 'Brand Story Architecture', 'Content Marketing Mastery', 'SEO in 2025', 'Paid Advertising ROI', 'Email Marketing Systems', 'Social Media Strategy', 'Conversion Rate Optimization', 'Analytics & Attribution', 'Your 90-Day Marketing Plan'],
      quote: 'People don\'t buy what you do — they buy why you do it.', quoteAuthor: 'Simon Sinek',
    },
  };

  return map[category] ?? map['Business & Entrepreneurship'];
}

// ─── Individual Page Renderers ─────────────────────────────────────────────────

function SpreadCoverPage({ template }: { template: EbookTemplate }) {
  return (
    <div className="w-full h-full flex flex-col bg-white relative overflow-hidden">
      <img src={template.image} alt={template.name} className="w-full h-full object-cover absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
      <div className="absolute inset-0 flex flex-col justify-end p-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white mb-4 w-fit" style={{ backgroundColor: template.color + 'cc' }}>
          <i className="ri-bookmark-line"></i> {template.category}
        </div>
        <h1 className="text-3xl font-black text-white leading-tight mb-3">{template.name}</h1>
        <p className="text-white/75 text-sm leading-relaxed mb-5 line-clamp-3">{template.description}</p>
        <div className="flex items-center gap-5 text-white/70 text-xs border-t border-white/20 pt-4">
          <span className="flex items-center gap-1.5"><i className="ri-book-open-line"></i>{template.chapters} chapters</span>
          <span className="flex items-center gap-1.5"><i className="ri-pages-line"></i>{template.pages} pages</span>
          <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>~{Math.round(template.pages / 25)}h read</span>
          <span className="flex items-center gap-1.5 ml-auto"><i className="ri-star-fill text-amber-400"></i> {template.rating}/5</span>
        </div>
      </div>
    </div>
  );
}

function SpreadTocPage({ template, content }: { template: EbookTemplate; content: PageContent }) {
  return (
    <div className="w-full h-full bg-white p-8 flex flex-col">
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: template.color }}></div>
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: template.color }}>Contents</span>
        </div>
        <h2 className="text-2xl font-black text-slate-900">Table of Contents</h2>
      </div>
      <div className="flex-1 space-y-0 overflow-hidden">
        {content.tocChapters.slice(0, template.chapters > 10 ? 10 : template.chapters).map((ch, i) => (
          <div key={i} className="flex items-center gap-3 py-2.5 border-b border-slate-100 group">
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-extrabold text-white flex-shrink-0" style={{ backgroundColor: template.color + (i % 2 === 0 ? 'dd' : '99') }}>
              {i + 1}
            </div>
            <span className="flex-1 text-sm text-slate-700 font-medium truncate">{ch}</span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="h-px w-8 bg-slate-200"></div>
              <span className="text-xs text-slate-400 font-mono w-6 text-right">{(i + 1) * 12 + Math.floor(Math.random() * 8)}</span>
            </div>
          </div>
        ))}
        {template.chapters > 10 && (
          <div className="py-2 text-center">
            <span className="text-xs text-slate-400 font-semibold">+ {template.chapters - 10} additional chapters</span>
          </div>
        )}
      </div>
      <div className="flex-shrink-0 mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <i className="ri-information-line"></i>
          <span>{template.pages} pages · Published {template.dateAdded}</span>
        </div>
      </div>
    </div>
  );
}

function SpreadIntroPage({ template, content }: { template: EbookTemplate; content: PageContent }) {
  return (
    <div className="w-full h-full bg-white p-8 flex flex-col">
      <div className="mb-5 flex-shrink-0">
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: template.color }}>Preface</span>
        <h2 className="text-xl font-black text-slate-900 mt-1">Introduction</h2>
      </div>
      <div className="flex-1 overflow-hidden space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">{content.introText}</p>
        <blockquote className="border-l-4 pl-4 py-1" style={{ borderColor: template.color }}>
          <p className="text-sm italic font-semibold text-slate-800 leading-relaxed">&ldquo;{content.introHighlight}&rdquo;</p>
        </blockquote>
        <div className="rounded-2xl p-4" style={{ backgroundColor: template.color + '0d' }}>
          <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: template.color }}>What You Will Learn</p>
          <ul className="space-y-2">
            {['Proven frameworks used by top performers', 'Step-by-step actionable strategies', 'Real-world case studies & examples', 'Practical tools and templates included'].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <i className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5" style={{ color: template.color }}></i>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-3 gap-2 flex-shrink-0">
          {[{ icon: 'ri-book-open-line', val: template.chapters, label: 'Chapters' }, { icon: 'ri-pages-line', val: template.pages, label: 'Pages' }, { icon: 'ri-time-line', val: `${Math.round(template.pages / 25)}h`, label: 'Read Time' }].map(s => (
            <div key={s.label} className="text-center p-2 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1" style={{ color: template.color }}>
                <i className={`${s.icon} text-base`}></i>
              </div>
              <p className="font-extrabold text-slate-900 text-base">{s.val}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SpreadChapterPage({ template, content }: { template: EbookTemplate; content: PageContent }) {
  return (
    <div className="w-full h-full bg-white p-8 flex flex-col">
      <div className="mb-5 flex-shrink-0">
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: template.color }}>{content.chapterSubtitle}</span>
        <h2 className="text-xl font-black text-slate-900 mt-1 leading-tight">{content.chapterTitle}</h2>
        <div className="w-12 h-0.5 rounded-full mt-3" style={{ backgroundColor: template.color }}></div>
      </div>
      <div className="flex-1 overflow-hidden space-y-3">
        <p className="text-xs text-slate-500 leading-relaxed italic">{content.introText.slice(0, 160)}...</p>
        <div className="space-y-2">
          {content.sections.map((section, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: i % 2 === 0 ? template.color + '08' : '#f8fafc' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold text-white flex-shrink-0" style={{ backgroundColor: template.color + (i < 2 ? 'ee' : '88') }}>
                {i + 1}
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">{section}</p>
                <p className="text-xs text-slate-500 mt-0.5">See page {20 + i * 8}–{27 + i * 8}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 rounded-xl" style={{ backgroundColor: template.color + '12' }}>
          <p className="text-xs font-bold mb-1" style={{ color: template.color }}>Chapter Insight</p>
          <p className="text-xs text-slate-600 italic leading-relaxed">&ldquo;{content.quote}&rdquo;</p>
          <p className="text-xs text-slate-400 mt-1">— {content.quoteAuthor}</p>
        </div>
      </div>
    </div>
  );
}

function SpreadDataPage({ template, content }: { template: EbookTemplate; content: PageContent }) {
  const bars = [72, 88, 65, 95, 80, 58, 91];
  const barLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
  return (
    <div className="w-full h-full bg-white p-8 flex flex-col">
      <div className="mb-5 flex-shrink-0">
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: template.color }}>Analytics</span>
        <h2 className="text-xl font-black text-slate-900 mt-1">{content.dataLabel}</h2>
      </div>
      <div className="flex-1 overflow-hidden space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {content.dataStats.map(s => (
            <div key={s.label} className="p-3 rounded-xl" style={{ backgroundColor: template.color + '0d' }}>
              <p className="text-xs text-slate-500 mb-0.5">{s.label}</p>
              <p className="text-xl font-extrabold" style={{ color: template.color }}>{s.val}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.trend}</p>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 mb-3">Performance Trend</p>
          <div className="flex items-end gap-1.5 h-20">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-sm" style={{ height: `${h}%`, backgroundColor: template.color, opacity: 0.6 + (i * 0.056) }}></div>
                <span className="text-xs text-slate-400">{barLabels[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-up-circle-line text-emerald-500 text-base"></i>
            </div>
            <p className="text-xs font-bold text-emerald-700">Overall trend is strongly positive across all key indicators.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpreadSummaryPage({ template, content }: { template: EbookTemplate; content: PageContent }) {
  return (
    <div className="w-full h-full bg-white p-8 flex flex-col">
      <div className="mb-5 flex-shrink-0">
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: template.color }}>Final Chapter</span>
        <h2 className="text-xl font-black text-slate-900 mt-1">Your Action Plan</h2>
      </div>
      <div className="flex-1 overflow-hidden space-y-3">
        {content.summaryPoints.map((point, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: template.color }}>
              <i className={`${point.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">{point.title}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{point.desc}</p>
            </div>
          </div>
        ))}
        <div className="p-4 rounded-2xl text-center" style={{ background: `linear-gradient(135deg, ${template.color}25, ${template.color}10)` }}>
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center mx-auto mb-2 text-white text-lg" style={{ backgroundColor: template.color }}>
            <i className="ri-trophy-line"></i>
          </div>
          <p className="text-sm font-extrabold text-slate-900 mb-1">Start Your Journey Today</p>
          <p className="text-xs text-slate-500 leading-relaxed">Use this template to share your expertise and impact thousands of readers worldwide.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Spread definitions ────────────────────────────────────────────────────────
type SpreadType = 'cover' | 'toc-intro' | 'chapter-data' | 'summary';

const SPREADS: { id: SpreadType; label: string }[] = [
  { id: 'cover', label: 'Cover' },
  { id: 'toc-intro', label: 'Contents & Intro' },
  { id: 'chapter-data', label: 'Chapter & Data' },
  { id: 'summary', label: 'Summary' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EbookFullScreenPreview({ template, onClose, onUse, isFavorite, onToggleFavorite }: Props) {
  const [spreadIdx, setSpreadIdx] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [animating, setAnimating] = useState(false);
  const [readingMode, setReadingMode] = useState(false);
  const content = getContent(template.category);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const navigate = useCallback((dir: 'next' | 'prev') => {
    if (animating) return;
    const next = dir === 'next' ? spreadIdx + 1 : spreadIdx - 1;
    if (next < 0 || next >= SPREADS.length) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => { setSpreadIdx(next); setAnimating(false); }, 280);
  }, [animating, spreadIdx]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') navigate('next');
      if (e.key === 'ArrowLeft') navigate('prev');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, navigate]);

  const renderLeftPage = () => {
    switch (SPREADS[spreadIdx].id) {
      case 'cover': return <SpreadCoverPage template={template} />;
      case 'toc-intro': return <SpreadTocPage template={template} content={content} />;
      case 'chapter-data': return <SpreadChapterPage template={template} content={content} />;
      case 'summary': return <SpreadDataPage template={template} content={content} />;
    }
  };

  const renderRightPage = () => {
    switch (SPREADS[spreadIdx].id) {
      case 'cover': return <SpreadTocPage template={template} content={content} />;
      case 'toc-intro': return <SpreadIntroPage template={template} content={content} />;
      case 'chapter-data': return <SpreadDataPage template={template} content={content} />;
      case 'summary': return <SpreadSummaryPage template={template} content={content} />;
    }
  };

  const pageProgress = (spreadIdx / (SPREADS.length - 1)) * 100;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: readingMode ? '#1a0f05' : '#020617' }}>

      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-3.5 border-b flex-shrink-0"
        style={{ backgroundColor: readingMode ? '#2a1a08' : '#0f172a', borderColor: readingMode ? '#4a3018' : '#1e293b' }}
      >
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer transition-colors" style={{ backgroundColor: readingMode ? '#3d2410' : '#1e293b' }}>
            <i className="ri-close-line text-white text-base"></i>
          </button>
          <div className="w-px h-6" style={{ backgroundColor: readingMode ? '#5a3a1a' : '#334155' }}></div>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: template.color }}>
            <i className="ri-book-2-line text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-extrabold text-white leading-none line-clamp-1">{template.name}</p>
            <p className="text-xs mt-0.5" style={{ color: readingMode ? '#c4956a' : '#64748b' }}>{template.category}</p>
          </div>
          {template.badge && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold hidden sm:inline-flex ${template.badge === 'Popular' ? 'bg-amber-500/20 text-amber-400' : template.badge === 'New' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
              {template.badge}
            </span>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-4 text-xs" style={{ color: readingMode ? '#c4956a' : '#64748b' }}>
          <span className="flex items-center gap-1.5"><i className="ri-star-fill text-amber-400"></i> {template.rating}</span>
          <span className="flex items-center gap-1.5"><i className="ri-book-open-line"></i> {template.chapters} chapters</span>
          <span className="flex items-center gap-1.5"><i className="ri-pages-line"></i> {template.pages} pages</span>
          <span className="flex items-center gap-1.5"><i className="ri-download-line"></i> {template.downloads.toLocaleString()} uses</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Reading Mode Toggle */}
          <button
            onClick={() => setReadingMode(v => !v)}
            title={readingMode ? 'Exit Reading Mode' : 'Enter Reading Mode (Sepia)'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
            style={readingMode
              ? { backgroundColor: '#d4881a', borderColor: '#d4881a', color: 'white' }
              : { backgroundColor: 'transparent', borderColor: '#334155', color: '#94a3b8' }
            }
          >
            <i className={`${readingMode ? 'ri-sun-fill' : 'ri-sun-line'} text-sm`}></i>
            <span className="hidden sm:inline">{readingMode ? 'Reading Mode' : 'Read Mode'}</span>
          </button>

          <div className="hidden sm:flex items-center gap-1 text-xs" style={{ color: readingMode ? '#8a6040' : '#64748b' }}>
            <kbd className="px-1.5 py-0.5 rounded font-mono text-xs" style={{ backgroundColor: readingMode ? '#3d2410' : '#1e293b', borderColor: readingMode ? '#5a3a1a' : '#334155', border: '1px solid' }}>←→</kbd>
            <span>navigate</span>
          </div>
          <button
            onClick={() => onToggleFavorite(template.id)}
            className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${isFavorite ? 'bg-rose-500/20 border-rose-500/50 text-rose-400' : 'border-slate-700 text-slate-400 hover:border-rose-500/50 hover:text-rose-400'}`}
          >
            <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
          </button>
          <button
            onClick={onUse}
            className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-sm text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ backgroundColor: template.color }}
          >
            <i className="ri-edit-line text-sm"></i>
            Use Template
          </button>
        </div>
      </div>

      {/* ── Main Reading Area ──────────────────────────────────────────────── */}
      <div
        className="flex-1 flex items-center justify-center relative overflow-hidden min-h-0 px-16 py-6"
        style={{ backgroundColor: readingMode ? '#1a0f05' : '#020617' }}
      >

        {/* Left Arrow */}
        <button
          onClick={() => navigate('prev')}
          disabled={spreadIdx === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border text-white flex items-center justify-center cursor-pointer transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
          style={{ backgroundColor: readingMode ? 'rgba(180,100,20,0.15)' : 'rgba(255,255,255,0.1)', borderColor: readingMode ? 'rgba(180,100,20,0.3)' : 'rgba(255,255,255,0.2)' }}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => navigate('next')}
          disabled={spreadIdx === SPREADS.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border text-white flex items-center justify-center cursor-pointer transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
          style={{ backgroundColor: readingMode ? 'rgba(180,100,20,0.15)' : 'rgba(255,255,255,0.1)', borderColor: readingMode ? 'rgba(180,100,20,0.3)' : 'rgba(255,255,255,0.2)' }}
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        {/* Book Spread — sepia filter applied here when reading mode on */}
        <div
          className="flex shadow-2xl"
          style={{
            transition: animating ? 'transform 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.28s' : 'none',
            opacity: animating ? 0 : 1,
            transform: animating ? `translateX(${direction === 'next' ? '-24px' : '24px'})` : 'translateX(0)',
            filter: readingMode ? 'sepia(0.35) saturate(0.85) brightness(0.96)' : 'none',
          }}
        >
          {/* Left Page */}
          <div
            className="overflow-hidden flex-shrink-0"
            style={{
              width: '360px', height: '510px',
              borderRadius: '4px 0 0 4px',
              boxShadow: 'inset -4px 0 12px rgba(0,0,0,0.12)',
              backgroundColor: readingMode ? '#f9eddb' : 'white',
            }}
          >
            {renderLeftPage()}
          </div>

          {/* Spine / Binding */}
          <div
            className="w-4 flex-shrink-0 relative"
            style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.2), rgba(0,0,0,0.05), rgba(0,0,0,0.15))' }}
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-black/20"></div>
          </div>

          {/* Right Page */}
          <div
            className="overflow-hidden flex-shrink-0"
            style={{
              width: '360px', height: '510px',
              borderRadius: '0 4px 4px 0',
              boxShadow: 'inset 4px 0 12px rgba(0,0,0,0.08)',
              backgroundColor: readingMode ? '#f9eddb' : 'white',
            }}
          >
            {renderRightPage()}
          </div>
        </div>

        {/* Spread label float */}
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm rounded-full border"
          style={{ backgroundColor: readingMode ? 'rgba(60,30,0,0.6)' : 'rgba(0,0,0,0.4)', borderColor: readingMode ? 'rgba(180,100,20,0.3)' : 'rgba(255,255,255,0.1)' }}
        >
          <span className="text-xs" style={{ color: readingMode ? '#c4956a' : 'rgba(255,255,255,0.6)' }}>Spread {spreadIdx + 1} of {SPREADS.length}</span>
          <span style={{ color: readingMode ? '#7a5030' : 'rgba(255,255,255,0.3)' }}>·</span>
          <span className="text-xs font-semibold" style={{ color: readingMode ? '#f0c090' : 'rgba(255,255,255,0.8)' }}>{SPREADS[spreadIdx].label}</span>
          {readingMode && (
            <span className="flex items-center gap-1 ml-1 text-xs" style={{ color: '#d4881a' }}>
              <i className="ri-sun-fill text-xs"></i>
              Sepia
            </span>
          )}
        </div>
      </div>

      {/* ── Bottom: Progress + Thumbnails ─────────────────────────────────── */}
      <div className="flex-shrink-0 border-t" style={{ backgroundColor: readingMode ? '#2a1a08' : '#0f172a', borderColor: readingMode ? '#4a3018' : '#1e293b' }}>
        {/* Progress bar */}
        <div className="h-0.5" style={{ backgroundColor: readingMode ? '#4a3018' : '#1e293b' }}>
          <div className="h-full transition-all duration-500" style={{ width: `${pageProgress}%`, backgroundColor: readingMode ? '#d4881a' : template.color }}></div>
        </div>

        <div className="px-6 py-3 flex items-center gap-4">
          <span className="text-xs flex-shrink-0 w-24" style={{ color: readingMode ? '#8a6040' : '#64748b' }}>Pages preview</span>
          <div className="flex gap-3 overflow-x-auto flex-1" style={{ scrollbarWidth: 'none' }}>
            {SPREADS.map((spread, i) => (
              <button
                key={spread.id}
                onClick={() => { setDirection(i > spreadIdx ? 'next' : 'prev'); setSpreadIdx(i); }}
                className="flex-shrink-0 flex flex-col items-center gap-1.5 cursor-pointer group"
              >
                <div
                  className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === spreadIdx ? 'scale-110 shadow-lg' : 'opacity-60 hover:opacity-90'}`}
                  style={{ borderColor: i === spreadIdx ? (readingMode ? '#d4881a' : template.color) : 'transparent' }}
                >
                  {i === 0 ? (
                    <img src={template.image} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col p-1.5" style={{ backgroundColor: readingMode ? '#f9eddb20' : template.color + '15' }}>
                      <div className="w-full h-1 rounded-full mb-1" style={{ backgroundColor: readingMode ? '#d4881a' : template.color }}></div>
                      {[70, 50, 60, 45].map((w, j) => (
                        <div key={j} className="h-1 rounded-full mb-0.5" style={{ width: `${w}%`, backgroundColor: readingMode ? '#6a4020' : '#cbd5e1' }}></div>
                      ))}
                    </div>
                  )}
                </div>
                <span
                  className="text-xs font-semibold whitespace-nowrap transition-colors"
                  style={{ color: i === spreadIdx ? (readingMode ? '#d4881a' : template.color) : (readingMode ? '#6a4020' : '#64748b') }}
                >
                  {spread.label}
                </span>
              </button>
            ))}
          </div>
          <div className="flex-shrink-0 flex items-center gap-1.5 text-xs" style={{ color: readingMode ? '#6a4020' : '#64748b' }}>
            <span>{spreadIdx + 1}/{SPREADS.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
